import { motion } from "framer-motion";
import { ArrowLeft, Search, Send, AlertTriangle } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { EmailTabNav } from "@/components/admin/email/EmailTabNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { EMAIL_CATEGORY_CONFIG, EMAIL_PRIORITY_CONFIG } from "@/types/email";
import type { EmailTemplateConfig, EmailCategory, EmailPriority } from "@/types/email";

interface Recipient {
  email: string;
  name: string;
  type: "student" | "partner" | "external";
  meta?: Record<string, unknown>;
}

export default function AdminEmailSendTemplate() {
  const { key } = useParams<{ key: string }>();
  const { toast } = useToast();

  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [variables, setVariables] = useState<Record<string, unknown>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [freeEmail, setFreeEmail] = useState("");
  const [freeName, setFreeName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: template } = useQuery({
    queryKey: ["email-template", key],
    queryFn: async () => {
      const { data, error } = await supabase.from("email_templates_config").select("*").eq("template_key", key).single();
      if (error) throw error;
      return data as EmailTemplateConfig;
    },
    enabled: !!key,
  });

  // Init defaults
  useEffect(() => {
    if (template?.preview_defaults) setVariables(template.preview_defaults as Record<string, unknown>);
  }, [template]);

  // Smart fill
  useEffect(() => {
    if (!recipient || !template?.variables_schema) return;
    const schema = template.variables_schema;
    const filled: Record<string, unknown> = { ...variables };
    if (recipient.type === "student" && recipient.meta) {
      if ("studentName" in schema) filled.studentName = recipient.name;
      if ("studentEmail" in schema) filled.studentEmail = recipient.email;
    }
    if (recipient.type === "partner" && recipient.meta) {
      if ("contactName" in schema) filled.contactName = recipient.name;
      if ("orgName" in schema) filled.orgName = recipient.meta.orgName;
    }
    setVariables(filled);
  }, [recipient, template]);

  const { data: students } = useQuery({
    queryKey: ["email-send-students", searchTerm],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("id, full_name, email, city, state").eq("role", "student").or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`).limit(10);
      return data;
    },
    enabled: searchTerm.length >= 2,
  });

  const { data: partners } = useQuery({
    queryKey: ["email-send-partners", searchTerm],
    queryFn: async () => {
      const { data } = await supabase.from("partner_users").select("id, contact_name, contact_email, partner_orgs(company_name)").or(`contact_name.ilike.%${searchTerm}%,contact_email.ilike.%${searchTerm}%`).limit(10);
      return data;
    },
    enabled: searchTerm.length >= 2,
  });

  const { data: previewHtml } = useQuery({
    queryKey: ["email-preview-render", key, variables],
    queryFn: async () => {
      const { data } = await supabase.functions.invoke("email-preview", { body: { templateKey: key, variables } });
      return data?.html as string | null;
    },
    enabled: !!key && !!recipient,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!recipient) throw new Error("Selecione um destinatário");
      const { data, error } = await supabase.functions.invoke("send-manual-email", {
        body: { templateKey: key, recipientEmail: recipient.email, recipientName: recipient.name, recipientType: recipient.type, variables },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: `Email enviado para ${recipient?.email}` });
      setShowConfirm(false);
      setRecipient(null);
      setConfirmed(false);
    },
    onError: (err) => toast({ title: "Erro", description: String(err), variant: "destructive" }),
  });

  if (!template) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl">
        <EmailTabNav />
        <div className="animate-pulse h-64 bg-muted rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <EmailTabNav />

      <Link to="/admin/emails/send" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Disparo Manual
      </Link>

      {/* Template info */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Disparar: {template.display_name}</h1>
        <p className="text-sm text-muted-foreground">{template.description}</p>
        <div className="flex gap-2 mt-2">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${EMAIL_PRIORITY_CONFIG[template.priority as EmailPriority]?.color}`}>
            {EMAIL_PRIORITY_CONFIG[template.priority as EmailPriority]?.label}
          </span>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${EMAIL_CATEGORY_CONFIG[template.category as EmailCategory]?.color}`}>
            {EMAIL_CATEGORY_CONFIG[template.category as EmailCategory]?.label}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Recipient + Variables */}
        <div className="space-y-6">
          {/* Recipient */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-6 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">01</span>
              <h3 className="text-base font-semibold">Destinatário</h3>
            </div>

            {recipient ? (
              <div className="bg-primary/5 border border-primary/10 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold">{recipient.name}</p>
                  <p className="text-xs text-muted-foreground">{recipient.email}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setRecipient(null)}>Trocar</Button>
              </div>
            ) : (
              <Tabs defaultValue="student">
                <TabsList className="grid w-full grid-cols-3 mb-3">
                  <TabsTrigger value="student">Aluno</TabsTrigger>
                  <TabsTrigger value="partner">Empresa</TabsTrigger>
                  <TabsTrigger value="free">Livre</TabsTrigger>
                </TabsList>
                <TabsContent value="student">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Nome ou email..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                  {students?.map((s) => (
                    <button key={s.id} onClick={() => { setRecipient({ email: s.email!, name: s.full_name || s.email!, type: "student", meta: { city: s.city, state: s.state } }); setSearchTerm(""); }} className="w-full text-left p-2 rounded-lg hover:bg-muted text-sm">
                      <span className="font-medium">{s.full_name}</span> <span className="text-muted-foreground text-xs">{s.email}</span>
                    </button>
                  ))}
                </TabsContent>
                <TabsContent value="partner">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Nome ou email..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                  {partners?.map((p: any) => (
                    <button key={p.id} onClick={() => { setRecipient({ email: p.contact_email, name: p.contact_name, type: "partner", meta: { orgName: p.partner_orgs?.company_name } }); setSearchTerm(""); }} className="w-full text-left p-2 rounded-lg hover:bg-muted text-sm">
                      <span className="font-medium">{p.contact_name}</span> <span className="text-muted-foreground text-xs">{p.contact_email}</span>
                    </button>
                  ))}
                </TabsContent>
                <TabsContent value="free" className="space-y-2">
                  <Input placeholder="Email" type="email" value={freeEmail} onChange={(e) => setFreeEmail(e.target.value)} />
                  <Input placeholder="Nome (opcional)" value={freeName} onChange={(e) => setFreeName(e.target.value)} />
                  <Button variant="outline" size="sm" disabled={!freeEmail} onClick={() => setRecipient({ email: freeEmail, name: freeName || freeEmail, type: "external" })}>Confirmar</Button>
                </TabsContent>
              </Tabs>
            )}
          </motion.div>

          {/* Variables */}
          {recipient && template.variables_schema && Object.keys(template.variables_schema).length > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">02</span>
                <h3 className="text-base font-semibold">Variáveis</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(template.variables_schema).map(([name, schema]) => (
                  <div key={name} className="space-y-1">
                    <label className="text-sm font-medium">{(schema as any).label || name}</label>
                    <Input value={String(variables[name] ?? "")} onChange={(e) => setVariables((p) => ({ ...p, [name]: e.target.value }))} />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {recipient && (
            <Button className="w-full" onClick={() => { setShowConfirm(true); setConfirmed(false); }}>
              <Send className="w-4 h-4 mr-2" /> Revisar e Enviar
            </Button>
          )}
        </div>

        {/* Right: Preview */}
        <div className="lg:sticky lg:top-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <span className="text-xs font-bold uppercase text-muted-foreground">Preview</span>
            </div>
            <div className="p-4">
              {previewHtml ? (
                <iframe srcDoc={previewHtml} sandbox="allow-same-origin" className="w-full h-[500px] bg-white border border-border rounded-xl" title="Preview" />
              ) : (
                <div className="h-[500px] flex items-center justify-center text-muted-foreground text-sm">
                  {recipient ? "Carregando preview..." : "Selecione um destinatário para ver o preview"}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Confirm dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Confirmar envio</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-xl space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Template:</span><span className="font-semibold">{template.display_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Para:</span><span className="font-semibold">{recipient?.email}</span></div>
            </div>
            {recipient?.type === "external" && (
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-blue-800">Destinatário externo.</p>
              </div>
            )}
            <label className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-xl cursor-pointer">
              <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" />
              <span className="text-sm font-medium">Confirmo que quero enviar este email.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfirm(false)}>Voltar</Button>
            <Button disabled={!confirmed || sendMutation.isPending} onClick={() => sendMutation.mutate()}>
              <Send className="w-4 h-4 mr-2" /> {sendMutation.isPending ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
